import { cookieService } from "@/lib/cookie/cookieService";
import { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";
import { AppError } from "@/lib/utils/errors";
import { isClient } from "@/lib/utils/client";

const BASE_ENDPOINT = process.env.NEXT_PUBLIC_API_ENDPOINT || "";

interface FetchOptions extends RequestInit {
  params?: Record<string, string>;
}

export class ApiClient {
  private readonly endpoint: string;
  private accessTokenAlreadySet: boolean = false;
  private lang: string = "";
  private accessToken: string | null = null;
  private cookies?: (() => Promise<ReadonlyRequestCookies>) | undefined;

  constructor(endpoint?: string) {
    this.endpoint = endpoint || BASE_ENDPOINT;
    if (isClient()) {
      this.getAccessToken().catch(() => {});
    }
  }

  setCookies(cookies: (() => Promise<ReadonlyRequestCookies>) | undefined) {
    if (isClient()) {
      this.cookies = cookies;
    }
  }

  private async getCookiesStore() {
    if (isClient() && this.cookies) return this.cookies;

    try {
      const { cookies: nextCookies } = await import("next/headers");
      return nextCookies;
    } catch (err) {
      const appError = new AppError(err);
      appError.logError("ApiClient.getCookiesStore error");
      return undefined;
    }
  }

  async getLang(): Promise<string> {
    if (isClient()) {
      this.lang = cookieService.get("i18next") || "vi";
      return this.lang;
    } else {
      const cookiesStore = await this.getCookiesStore();
      return (await cookieService.getServer("i18next", cookiesStore)) || "vi";
    }
  }

  setToken(token: string | null) {
    if (isClient()) {
      this.accessTokenAlreadySet = true;
      this.accessToken = token;
    }
  }

  async getToken() {
    return await this.getAccessToken();
  }

  async getAccessToken() {
    if (isClient()) {
      if (
        (!this.accessToken || this.accessToken.length === 0) &&
        !this.accessTokenAlreadySet
      ) {
        const accessToken =
          cookieService.get("token", { encrypt: true }) || null;
        this.accessToken = accessToken;
        this.accessTokenAlreadySet = true;
      }
      return this.accessToken;
    } else {
      const cookiesStore = await this.getCookiesStore();

      return (
        (await cookieService.getServer("token", cookiesStore, undefined, {
          encrypt: true,
        })) || null
      );
    }
  }

  async fetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const lang = await this.getLang();
    const accessToken = await this.getAccessToken();
    const { params, ...fetchOptions } = options;

    // Add query params if they exist
    const url = new URL(`${endpoint.length > 0 ? endpoint : this.endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    // Default headers
    const headers = {
      "x-language": lang,
      ...fetchOptions.headers,
    };
    if (accessToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${accessToken}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });
    if (!response.ok) {
      let errorData = {
        code: response.status,
        message: response.statusText || "Request failed",
        reason: response.statusText || "",
        details: [] as any[],
      };
      try {
        const error = await response.json();
        if (error && typeof error === "object") {
          errorData = {
            code: error.code || response.status,
            message: error.message || response.statusText,
            reason: error.reason || error.message || response.statusText,
            details: error.details || [],
          };
        }
      } catch {
        // Body is not JSON (e.g. 502/504 Bad Gateway HTML from proxy)
      }
      throw new AppError(errorData);
    }
    return response.json();
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const apiClient = new ApiClient();
